import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { articles } from '@/constants/journal'
import { ArticleRow } from '@/components/journal/article-card'
import { RowPreview } from '@/components/journal/row-preview'

/* The index is a ruled register, not a photo grid. Rows stagger in with their
   rules drawing from the left, which is where the life comes from now that
   there is no cover image on each entry. */
export const JournalGrid: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root || prefersReducedMotion()) {
            return
        }

        gsap.fromTo('[data-journal-rule]',
            { scaleX: 0 },
            {
                scaleX: 1,
                duration: 1.15,
                stagger: 0.06,
                transformOrigin: 'left',
                ease: 'noma',
                scrollTrigger: { trigger: root, start: 'top 88%', once: true }
            }
        )

        gsap.fromTo('[data-journal-row] a',
            { opacity: 0, y: 28 },
            {
                opacity: 1,
                y: 0,
                duration: 1,
                stagger: 0.055,
                ease: 'noma',
                scrollTrigger: { trigger: root, start: 'top 88%', once: true }
            }
        )
    }, scope, [])

    return (
        <section
            ref={scope}
            id='journal-articles'
            aria-label='All articles'
            className='mx-auto w-full max-w-[1920px] px-5 pb-16 text-ink lg:px-[33px] lg:pb-[104px]'
        >
            <ul className='flex flex-col'>
                {articles.map((article, index) => (
                    <ArticleRow key={article.slug} article={article} index={index} />
                ))}

                <span
                    data-journal-rule
                    aria-hidden='true'
                    className='block h-px w-full origin-left bg-current opacity-15'
                />
            </ul>

            <RowPreview />
        </section>
    )
}
